// ============================================================
// Add Protocol v3 - administration write helpers
//
// One physical injection = one administration. A single-peptide dose
// writes 1 DoseLog row; a combined-vial dose writes N per-component
// rows sharing ONE administrationId (spec C3/E9/E10). This module is
// the ONE place that turns a ScheduledDose into its log rows, so the
// HomePage, DesktopDashboard, LogDoseDrawer, and notification action
// paths can never disagree about group identity.
//
// React-free on purpose: skipFromNotification runs in the background
// notification listener outside any component.
// ============================================================

import { uuid } from '../utils/uuid';
import { InjectionSite } from './types';
import type { DoseLog, ScheduledDose } from './types';
import { addDoseLogs } from './cloud/doseLogs';

export interface AdministrationLogOptions {
  loggedTime: string;
  injectionSite: InjectionSite;
  skipped: boolean;
  notes?: string;
  /** Full ISO timestamp; omit when the UI has no scheduled point. */
  scheduledTime?: string;
}

/**
 * Build the DoseLog rows for one administration of a ScheduledDose.
 * Singles produce exactly one row; combined groups produce one row
 * per component with the TRUE per-peptide doseMcg from the group
 * breakdown. Every row (singles included, per E10) is stamped with a
 * fresh shared administrationId.
 *
 * Group row units are the component's proportional share of the draw
 * (shares sum to the drawn units), so per-row unit displays stay
 * physically truthful.
 */
export function buildAdministrationLogs(
  dose: ScheduledDose,
  opts: AdministrationLogOptions,
): DoseLog[] {
  const administrationId = uuid();
  const createdAt = new Date().toISOString();
  const common = {
    protocolId: dose.protocolId,
    injectionSite: opts.injectionSite,
    scheduledTime: opts.scheduledTime,
    loggedTime: opts.loggedTime,
    notes: opts.notes,
    skipped: opts.skipped,
    administrationId,
    createdAt,
  };

  if (!dose.mixGroupId || !dose.groupComponents || dose.groupComponents.length === 0) {
    return [{
      id: uuid(),
      componentId: dose.componentId,
      phaseId: dose.phaseId,
      peptideId: dose.peptideId,
      peptideName: dose.peptideName,
      doseMcg: dose.doseMcg,
      units: dose.units,
      ...common,
    }];
  }

  const totalMcg = dose.doseMcg;
  return dose.groupComponents.map((gc) => ({
    id: uuid(),
    componentId: gc.componentId,
    phaseId: gc.phaseId,
    peptideId: gc.peptideId,
    peptideName: gc.peptideName,
    doseMcg: gc.doseMcg,
    units: totalMcg > 0
      ? Math.round(dose.units * (gc.doseMcg / totalMcg) * 10) / 10
      : 0,
    ...common,
  }));
}

/**
 * Collapse per-component DoseLog rows back into ONE entry per physical
 * administration, for timeline displays (dashboard "Logged today",
 * History date view). Rows sharing an administrationId merge: peptide
 * names join with " + ", doseMcg sums, and units sum - unit shares are
 * proportional splits of the draw (see buildAdministrationLogs), so
 * their sum IS the real drawn units. Legacy rows without an
 * administrationId pass through untouched. Input order is preserved;
 * the first row of an administration anchors its position and provides
 * id/time/site/skipped for the merged entry.
 *
 * NOT for per-peptide views (History "by peptide"): there a combo's
 * component rows correctly live under their own peptide buckets.
 */
export function collapseAdministrations(logs: DoseLog[]): DoseLog[] {
  const out: DoseLog[] = [];
  const byAdmin = new Map<string, DoseLog>();
  for (const log of logs) {
    const key = log.administrationId;
    if (!key) {
      out.push(log);
      continue;
    }
    const merged = byAdmin.get(key);
    if (!merged) {
      const first = { ...log };
      byAdmin.set(key, first);
      out.push(first);
      continue;
    }
    merged.peptideName = `${merged.peptideName} + ${log.peptideName}`;
    merged.doseMcg = (merged.doseMcg || 0) + (log.doseMcg || 0);
    merged.units = Math.round(((merged.units || 0) + (log.units || 0)) * 10) / 10;
  }
  return out;
}

/**
 * THE shared skip path (E9): write skipped rows for one administration
 * - N rows sharing one administrationId for a combined group, 1 row
 * for a single. One PostgREST insert, no inventory write (skips never
 * consume doses). Used by the HomePage and Desktop skip buttons and
 * the notification Skip action.
 *
 * Returns the shared administrationId so callers can offer Undo
 * (deleteDoseLogsByAdministration reverses the whole set).
 */
export async function skipScheduledDose(
  dose: ScheduledDose,
  loggedTime: string,
  notes: string,
  scheduledTime?: string,
): Promise<string> {
  const logs = buildAdministrationLogs(dose, {
    loggedTime,
    injectionSite: InjectionSite.ABDOMEN_LEFT,
    skipped: true,
    notes,
    scheduledTime,
  });
  await addDoseLogs(logs);
  // buildAdministrationLogs stamps every row (E10), so the id is present.
  return logs[0].administrationId!;
}
